'use client';

// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step
import { Box, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// import the core library.
import ReactEChartsCore from 'echarts-for-react/lib/core';
// Import charts, all with Chart suffix
import { BarChart } from 'echarts/charts';
// import components, all suffixed with Component
import { GridComponent, TooltipComponent } from 'echarts/components';
// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';
import {
  //   CanvasRenderer,
  SVGRenderer,
} from 'echarts/renderers';
import { type FC, useEffect, useMemo, useState } from 'react';
// import { SecondCircleResultSchemaT } from 'src/utils/fetch-helio-second-circle-items';

type Props = {
  interestHierarchy: {
    name: string;
    level: number;
    interest: number;
  }[];
  levelHierarchy: {
    name: string;
    level: number;
    interest: number;
  }[];
  width?: string;
  height?: string;
  levelLabels?: string[];
  interestLabels?: string[];
  hasInterestFirst?: boolean;
  showSwitch?: boolean;
  isBlury?: boolean;
};

// Register the required components
echarts.use([TooltipComponent, GridComponent, BarChart, SVGRenderer]);

export const QuarkDomainsBarGraph: FC<Props> = (props: Props) => {
  const {
    interestHierarchy,
    levelHierarchy,
    isBlury = false,
    hasInterestFirst = false,
    showSwitch = false,
    width = '800px',
    height = '540px',
    levelLabels = ['Non évalué', 'Débutant', 'Intermédiaire', 'Avancé'],
    interestLabels = ['Aucun', 'Faible', 'Moyen', 'Fort'],
  } = props;
  const blurFilter = isBlury ? 'blur(4px)' : 'blur(0)';

  const [showInterest, setShowInterest] = useState(hasInterestFirst);
  const [items, setItems] = useState(
    hasInterestFirst ? interestHierarchy : levelHierarchy
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowInterest(event.target.checked);
    setItems(event.target.checked ? interestHierarchy : levelHierarchy);
  };

  const [levelOpacity, setLevelOpacity] = useState(showInterest ? 0.4 : 1);
  const [interestOpacity, setInterestOpacity] = useState(
    showInterest ? 1 : 0.4
  );

  useEffect(() => {
    setLevelOpacity(showInterest ? 0.4 : 1);
    setInterestOpacity(showInterest ? 1 : 0.4);
  }, [showInterest]);

  const theme = useTheme();

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          return `${params[0].name}<br />
          ${params[0].marker} ${params[0].seriesName} : ${
            levelLabels[Math.round(params[0].data)]
          }<br />
          ${params[1].marker} ${params[1].seriesName} : ${
            interestLabels[Math.round(params[1].data)]
          }`;
        },
      },
      grid: {
        containLabel: true,
      },
      // toolbox: {
      //   show: true,
      //   orient: 'vertical',
      //   left: 'right',
      //   top: 'center',
      //   feature: {
      //     mark: { show: true },
      //     dataView: { show: true, readOnly: false },
      //     magicType: { show: true, type: ['line', 'bar', 'stack'] },
      //     restore: { show: true },
      //     saveAsImage: { show: true }
      //   }
      // },
      xAxis: [
        {
          type: 'category',
          // axisTick: { show: true },
          axisLabel: {
            show: true,
            interval: 0,
            rotate: 45,
            fontWeight: 'bold',
          },
          data: items.map(
            (item: { name: string; level: number; interest: number }) =>
              item.name
          ),
        },
      ],
      yAxis: [
        {
          type: 'value',
          // name: 'Maîtrise',
          // nameTextStyle: {
          //   fontSize: 16,
          //   fontWeight: 'bolder',
          //   color: showInterest ? 'inherit' : theme.palette.primary.main,
          // },
          min: 0,
          max: 3,
          interval: 1,
          axisLabel: {
            formatter: (value: string, index: number) => {
              return levelLabels[index];
            },
          },
        },
        {
          type: 'value',
          // name: 'Intérêt',
          // nameTextStyle: {
          //   fontSize: 16,
          //   fontWeight: 'bold',
          //   color: showInterest ? theme.palette.primary.main : 'inherit',
          // },
          min: 0,
          max: 3,
          interval: 1,
          axisLabel: {
            formatter: (value: string, index: number) => {
              return `${interestLabels[index]} intérêt`;
            },
          },
        },
      ],
      series: [
        {
          name: 'Maîtrise',
          type: 'bar',
          barGap: 0,
          emphasis: {
            focus: 'series',
          },
          data: items.map(
            (item: { name: string; level: number; interest: number }) =>
              item.level
          ),
          itemStyle: {
            // borderWidth: 2,
            // borderColor: 'transparent',
            color: theme.palette.primary.main,
            opacity: levelOpacity,
          },
        },
        {
          name: 'Intérêt',
          type: 'bar',
          emphasis: {
            focus: 'series',
          },
          data: items.map(
            (item: { name: string; level: number; interest: number }) =>
              item.interest
          ),
          itemStyle: {
            // borderWidth: [0, 4, 0, 4],
            // borderColor: 'transparent',
            color: theme.palette.primary.main,
            opacity: interestOpacity,
          },
        },
      ],
    }),
    [levelLabels, interestLabels, levelOpacity, interestOpacity, items]
  );

  return (
    <Box component="div">
      {showSwitch && (
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Typography
            fontWeight="bolder"
            sx={{ color: showInterest ? 'inherit' : 'primary.main' }}
          >
            Maîtrise
          </Typography>
          <Switch
            checked={showInterest}
            onChange={handleChange}
            inputProps={{ 'aria-label': 'controlled' }}
          />
          <Typography
            fontWeight="bolder"
            sx={{ color: showInterest ? 'primary.main' : 'inherit' }}
          >
            Interêt
          </Typography>
        </Stack>
      )}
      <Box
        component="div"
        position="relative"
        width={width}
        height={height}
        sx={{ filter: blurFilter }}
      >
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          notMerge={true}
          lazyUpdate={true}
          theme={'theme_name'}
          style={{ height: '100%', width: '100%', position: 'relative' }}
          opts={{}}
        />
      </Box>
    </Box>
  );
};
